import { memo } from "react";
import { Search, Package, User, X, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type SearchMode = "item" | "player";

interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchMode: SearchMode;
  onModeChange: (mode: SearchMode) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

const SearchBar = memo(({ 
  searchQuery, 
  onSearchChange, 
  searchMode, 
  onModeChange,
  onRefresh,
  isRefreshing = false,
  className 
}: SearchBarProps) => {
  const toggleMode = () => {
    onModeChange(searchMode === "item" ? "player" : "item");
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Search Input with Mode Toggle on Left */}
      <div className="relative flex-1">
        {/* Mode Toggle - Left Side (replaces search icon) */}
        <button
          onClick={toggleMode}
          className={cn(
            "absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-colors z-10",
            "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
          )}
          aria-label={searchMode === "item" ? "Zu Spielersuche wechseln" : "Zu Itemsuche wechseln"}
          title={searchMode === "item" ? "Spieler suchen" : "Items suchen"}
        >
          {searchMode === "item" ? <Package size={16} /> : <User size={16} />}
        </button>
        
        <Input
          type="text"
          placeholder={searchMode === "item" ? "Item suchen..." : "Spieler suchen..."}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 pr-8 bg-secondary/30 border-border/50 h-9"
          maxLength={100}
        />
        
        {/* Clear Button - Right Side */}
        {searchQuery && (
          <button
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Suche leeren"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Refresh Button - directly attached */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className={cn(
            "flex items-center gap-2 px-3 h-9 rounded-lg transition-colors",
            "bg-secondary/30 border border-border/50 hover:bg-secondary/50",
            "text-muted-foreground hover:text-foreground disabled:opacity-50"
          )}
          aria-label="Auktionen aktualisieren"
        >
          <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
          <span className="hidden lg:inline text-sm">Aktualisieren</span>
        </button>
      )}
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
